import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Dialog, Transition } from '@headlessui/react'
import CdnImage from 'components/util/CdnImage'
// import { StaticImage } from 'gatsby-plugin-image'
import React, { Fragment } from 'react'
import BulletPointImage from '../../../images/BulletPoint.svg'

const RecipeOrTreatAlterModal = ({ show, setShow, activeRecipe }) => {
  const getTitle = (weight: string) => {
    if (weight.includes('ml')) {
      return 'Net volume'
    }
    if (weight.includes('cm')) {
      return 'Net size'
    }
    return 'Net weight'
  }

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={() => setShow(false)}>
        <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="relative inline-block max-w-md transform overflow-hidden rounded-2xl bg-white text-left align-bottom shadow-xl transition-all sm:my-8 md:w-full md:max-w-4xl md:align-middle">
              {activeRecipe?.images?.cover && (
                <div className="relative flex h-[220px] flex-col justify-center overflow-hidden md:h-[350px]">
                  <>
                    <CdnImage
                      url={activeRecipe?.images?.cover}
                      alt={activeRecipe?.name}
                      className="h-[24rem] w-full object-cover lg:h-[28rem]"
                      style={{
                        maxWidth: '100%',
                        objectFit: 'cover'
                      }}
                      steps={[896]}
                    />
                  </>
                </div>
              )}
              <div className="z-index-1 relative -mt-6 ml-[5%] w-[90%] lg:-mt-8 lg:ml-[30%] lg:-mt-6 lg:w-[41%]">
                {(activeRecipe?.adultOnly || activeRecipe?.itemId === 'RECP-SSFS') && (
                  <div
                    className={`mx-auto mt-1 ml-[5%] lg:ml-[0%] inline-flex w-[90%] justify-center rounded-full bg-[#D1D5DA] py-2 text-center font-sans font-semibold text-xs md:text-sm lg:w-full lg:px-2`}
                  >
                    {activeRecipe.adultOnly
                      ? 'Unsuitable for puppies & pregnant/nursing dogs'
                      : 'Not suitable as a sole diet for puppies. Unsuitable for pregnant or nursing dogs'}
                  </div>
                )}
              </div>
              <div className="px-4 md:px-8">
                <div className="w-full py-4 text-center md:py-8">
                  <Dialog.Title as="h2" className={`mt-6 text-xl leading-6 lg:mt-4 !font-sans !font-semibold`}>
                    {activeRecipe?.name}
                  </Dialog.Title>
                  <div className="mt-4">
                    <p className="text-sm">{activeRecipe?.description}</p>
                  </div>
                  {activeRecipe?.bullets && activeRecipe?.bullets?.length > 0 && (
                    <ul className="mt-4 block w-full items-center justify-center gap-x-4 md:flex-row ">
                      {activeRecipe?.bullets.map((bullet: string) => (
                        <li
                          key={`recipe-${bullet}`}
                          className="mx-1 inline-flex flex-1 items-center justify-center md:flex-initial"
                        >
                          <img width={20} src={BulletPointImage} alt={bullet} />
                          <span className="ml-2 text-sm">{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <button
                  className="absolute right-4 top-4"
                  onClick={() => {
                    setShow(false)
                  }}
                >
                  <FontAwesomeIcon
                    className="h-8 w-8 cursor-pointer rounded-full bg-white p-1"
                    icon={faXmark}
                    fontSize={35}
                  />
                </button>
                <div className="flex flex-col-reverse gap-0 border-t border-[#C4C4C4] py-4 md:flex-row md:py-8 lg:gap-6">
                  <div className="flex w-full items-center justify-center md:w-2/5">
                    {activeRecipe?.images?.primary && (
                      <CdnImage
                        url={activeRecipe?.images?.primary}
                        alt={activeRecipe.name}
                        steps={[416, 330]}
                        className="hidden w-full rounded-md lg:block"
                      />
                    )}
                    {/* {activeRecipe?.images?.secondary && (
                      <CdnImage
                        url={activeRecipe?.images?.secondary}
                        alt={activeRecipe?.name}
                        steps={[416, 330]}
                        className="block w-full rounded-md"
                      />
                    )} */}
                    {!activeRecipe?.images?.noBackground && activeRecipe?.images?.primary && (
                      <CdnImage
                        url={activeRecipe?.images?.primary}
                        alt={activeRecipe?.name}
                        steps={[416, 330]}
                        className="block w-full lg:hidden"
                      />
                    )}
                  </div>
                  <div className="w-full md:w-3/5">
                    {(activeRecipe?.composition || activeRecipe?.additives || activeRecipe?.nutrients) && (
                      <div className="p-0 pt-4 text-left lg:py-4">
                        <h4 className={`text-center font-bold md:text-left !font-sans font-semibold`}>
                          Nutritional Information
                        </h4>
                        {activeRecipe?.composition && (
                          <div className="mt-2 flex flex-col text-sm md:block">
                            <span className="font-bold">Ingredients: </span>
                            <span>
                              <span>{activeRecipe.composition} </span>
                              {activeRecipe?.calories && <span>{activeRecipe.calories} kcal/100g</span>}
                            </span>
                          </div>
                        )}
                        {activeRecipe?.feedingInformation && (
                          <div className="mt-2 flex flex-col text-sm md:block">
                            <span className="font-bold">Feeding information: </span>
                            <span>{activeRecipe.feedingInformation}</span>
                          </div>
                        )}
                        {activeRecipe?.additives && (
                          <div className="mt-2 flex flex-col text-sm md:block">
                            <span className="font-bold">Additives: </span>
                            <span>{activeRecipe.additives}</span>
                          </div>
                        )}
                        {activeRecipe?.nutrients && (
                          <div className="mt-2 flex flex-col text-sm md:block">
                            <span className="font-bold">Nutrients: </span>
                            <span>{activeRecipe.nutrients}</span>
                          </div>
                        )}
                        {activeRecipe?.weight && (
                          <div className="mt-6 flex flex-col text-sm md:block">
                            <div className={`h4 text-center text-base font-bold md:text-left !font-sans`}>
                              {getTitle(activeRecipe.weight)}
                            </div>
                            <span className="text-center font-bold md:text-left">{activeRecipe?.weight}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mx-auto mb-8 grid grid-cols-3">
                  <div className="col-start-2 inline-flex justify-center">
                    <button onClick={() => setShow(false)} className="btn-secondary">
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

export default RecipeOrTreatAlterModal
